import React from 'react'
import { TaskCard } from '.'
import { CardProps } from '../../Missions/Types/ZapValueIndex'
import { Span, Strong } from '../Typeography'
import { Box } from '../../UI/Box'
import {
  NewTaskType,
  SubTask,
  ZapLaunchMetaData,
  ZapLaunchSubType,
} from '../../Missions/types'
import styled, { css } from 'styled-components'
import ReactMarkdown from 'react-markdown'
import {
  CompletedCheckbox,
  ExpiredCheckbox,
} from '../../../page_components/DropDetails/ChapterView'
import Separator from '../Separator'

type CardPropsWithChildren = CardProps & {
  children: React.ReactNode
  actionText?: string
  isFaded?: boolean
  isRecurring?: boolean
  recurringTimeLeft?: string
  expiresIn?: string
  isExpired?: boolean
  multiplier?: number
  formattedReward?: string
  isHighlighted?: boolean
  recurringDetails?: {
    numberOfCompletions: number
    allowedCompletionsPerPeriod: number
    allTimeRecurringRewards: number
  }
  xpTosShow?: number
}

const Multiplier = styled.div`
  width: auto;
  height: auto;
  border-radius: 50%;
  border: 1px solid #ffff00;
  font-size: 6px;
  padding: 4px;
`

const SharedTaskCard = ({
  task,
  isComplete,
  projectStatus,
  rewardDisplay,
  onClick,
  actionText,
  isFaded,
  isRecurring,
  recurringTimeLeft,
  expiresIn,
  isExpired,
  multiplier,
  formattedReward,
  isHighlighted,
  recurringDetails,
  xpTosShow,
}: CardPropsWithChildren) => {
  const {
    pointReward,
    internalName: description,
    instructions,
    xpReward,
  } = task
  const subtask = task.subtasks[0]
  if (!subtask) return <></>
  const missionType = getMissionType(subtask, subtask?.onChainTask)
  const iconSrc = iconSrcReference[missionType]
  const isActive = !isComplete && !isExpired

  return (
    <TaskCard
      onClick={!isActive ? () => null : onClick}
      pad="m"
      isActive={isActive}
      isFaded={isFaded}
      isHighlighted={isHighlighted}
    >
      <Box g={12}>
        {/* {isComplete && (
          <CompletedCheckboxContainer>
            <CompletedCheckbox />
          </CompletedCheckboxContainer>
        )} */}
        <TypeIcon>
          <img
            src={
              description.toLowerCase() === 'download coinbase wallet'
                ? '/img/icons/task/coinbase-wallet.svg'
                : description.toLowerCase() === 'create a bybit wallet' ?
                  '/img/icons/task/bybit-wallet.png'
                  : iconSrc
            }
            alt={missionType}
            className="mission-type"
          />
        </TypeIcon>
        <DescriptionBox dc g={4} isComplete={isComplete}>
          <Strong s="L">{description}</Strong>
          <StyledMarkdown>
            {/* <Span s="S" c="text-secondary"> */}
            {instructions}
            {/* </Span> */}
          </StyledMarkdown>
        </DescriptionBox>
      </Box>
      {!isExpired && isRecurring && !!recurringTimeLeft && (
        <Box dc g={8}>
          <Box g={4} ac jsb>
            <Box g={4}>
              <img
                src="/img/icons/black-white-reset.svg"
                alt="recurring icon"
              />
              <Span s="S" c="text-secondary">
                Resets in
              </Span>
            </Box>
            <Strong s="S">{recurringTimeLeft}</Strong>
          </Box>
          <Separator marB={0} marT={0} />
        </Box>
      )}
      {recurringDetails && (
        <Box dc g={8}>
          <Box g={4} ac jsb>
            <Box g={4}>
              <img src="/img/icons/black-white-xp.svg" alt="xp icon" />
              <Span s="S" c="text-secondary">
                {xpReward ?? pointReward} per complete
              </Span>
            </Box>
            <Strong s="S">
              {recurringDetails.numberOfCompletions} of{' '}
              {recurringDetails.allowedCompletionsPerPeriod}
            </Strong>
          </Box>
          <Separator marB={0} marT={0} />
        </Box>
      )}
      {!isExpired && !isRecurring && !!expiresIn && (
        <Box dc g={8}>
          <Box g={4} ac jsb>
            <Span s="S" c="text-secondary">
              Ends in
            </Span>
            <Strong s="S">{expiresIn}</Strong>
          </Box>
          <Separator marB={0} marT={0} />
        </Box>
      )}
      <Box jsb ac fu>
        <Box>
          <Span s="S" c="text-secondary">
            Rewards
          </Span>
        </Box>
        <Box g={8} ac jfe>
          <RewardsBox fu aw g={4}>
            {isComplete && (
              <CompletedCheckboxContainer>
                <CompletedCheckbox w={12} h={12} />
              </CompletedCheckboxContainer>
            )}
            {!isComplete &&
              !recurringDetails?.allTimeRecurringRewards &&
              isExpired && (
                <CompletedCheckboxContainer>
                  <ExpiredCheckbox w={12} h={12} />
                </CompletedCheckboxContainer>
              )}
            <img src="/img/icons/xp.svg" alt="xp icon" />
            {recurringDetails ? (
              <Strong s="S">{recurringDetails.allTimeRecurringRewards}</Strong>
            ) : (
              <Strong s="S">
                {xpTosShow ?? formattedReward ?? xpReward ?? pointReward}
              </Strong>
            )}
          </RewardsBox>
          {!!multiplier && <Multiplier>{multiplier}X</Multiplier>}
        </Box>
      </Box>
    </TaskCard>
  )
}

const RewardsBox = styled(Box)`
  position: relative;
  background: #222222;
  padding: 6px 4px;
  border-radius: 20px;
`

const DescriptionBox = styled(Box) <{ isComplete?: boolean }>`
  ${({ isComplete }) =>
    isComplete &&
    css`
      opacity: 0.5;
    `}
`

const CompletedCheckboxContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
`

const StyledMarkdown = styled(ReactMarkdown)`
  p {
    font-size: 12px;
  }
`

const TypeIcon = styled.div`
  position: relative;
  border-radius:4px;
  img.mission-type {
    width: 64px;
    height: 64px;
  }
`

export function getMissionType(
  task: SubTask,
  onChainMission?: IOnChainMission,
): keyof typeof iconSrcReference {
  const {
    twitterTaskType,
    isEmailTask,
    isReferralTask,
    imageVerification,
    visitUrl,
    discordInviteLink,
    discordServerId,
    formMissionId,
    telegramUrl,
    isRollupMission,
    newTaskType,
  } = task

  if (newTaskType === NewTaskType.ZAP_LAUNCH) {
    const isCreatePool =
      (task?.metaData as ZapLaunchMetaData)?.type ===
      ZapLaunchSubType.CREATE_A_POOL
    const isInvestPool =
      (task?.metaData as ZapLaunchMetaData)?.type ===
      ZapLaunchSubType.INVEST_IN_POOL

    if (isCreatePool) return 'zap-launch-create-pool'
    if (isInvestPool) return 'zap-launch-invest-pool'
  }

  if (newTaskType === NewTaskType.TWITTER_VIEW) return 'twitter-views'

  if (newTaskType === NewTaskType.GRAPH_INDEXER) return 'graph-indexer'

  if (newTaskType === NewTaskType.VERIFY_WALLET) return 'verify-wallet'

  if (newTaskType === NewTaskType.MANUAL_API_REWARD) return 'manual-api-reward'

  if (newTaskType === NewTaskType.ZAP_VALUE_INDEX) return 'zap-value-index'

  if (!!onChainMission?.id) return 'on-chain'

  if (!!formMissionId) return 'form'
  if (isEmailTask) return 'email'
  if (isReferralTask) return 'referral'
  if (!!twitterTaskType) return 'twitter'
  if (!!discordInviteLink && !!discordServerId) return 'discord'
  if (!!telegramUrl) return 'telegram'
  if (!!isRollupMission) return 'rollup'

  const imageVerificationType = String(imageVerification)
  if (imageVerificationType === ImageVerification.INSTAGRAM) return 'instagram'
  if (imageVerificationType === ImageVerification.YOUTUBE) return 'youtube'
  if (imageVerificationType === ImageVerification.SPOTIFY) return 'spotify'
  if (imageVerificationType === ImageVerification.TIKTOK) return 'tiktok'

  if (!!visitUrl) return 'visit-link'

  return 'collect-review'
}

export default SharedTaskCard

export const ImageVerification = {
  YOUTUBE: 'youtube_verification',
  SPOTIFY: 'spotify_verification',
  TIKTOK: 'tiktok_verification',
  INSTAGRAM: 'instagram_verification',
}

export enum OnChainMissionType {
  VALIDATE_NFT_OWNERSIP = 'validate_nft_ownership',
  VALIDATE_TOKEN_OWNERSHIP = 'validate_token_ownership',
  VALIDATE_SMART_CONTRACT_STATE = 'validate_smart_contract_state',
  VALIDATE_SMART_CONTRACT_INTERACTION_STATE = 'validate_smart_contract_interaction',
}

export type InputArguments = {
  type: string
  name: string
  value: string
}[]

export type OutputArguments = {
  type: string
  name: string
  value: string
}[]

export enum OnChainMissionStakeTokenType {
  NATIVE_TOKEN_STAKE = 'native_token_stake',
  ERC20_TOKEN_STAKE = 'erc20_token_stake',
}

export interface OnChainMetaData {
  numberOfTransactions: number
  durationInDays: number
  stakedAmount?: number
  stakedTokenType?: OnChainMissionStakeTokenType
  stakedAmountVariableName?: string
  unstakeMethodName?: string
  unstakeAmountVariableName?: string
  decimals?: number
}

export interface IOnChainMission {
  id: string
  type: string
}

export const iconSrcReference = {
  email: '/img/icons/task/email.svg',
  twitter: '/img/icons/task/twitter.svg',
  'twitter-views': '/img/icons/task/twitter.svg',
  instagram: '/img/icons/task/instagram.png',
  youtube: '/img/icons/task/youtube.svg',
  'visit-link': '/img/icons/task/link.svg',
  spotify: '/img/icons/task/spotify.svg',
  discord: '/img/icons/task/discord.svg',
  form: '/img/icons/task/form.svg',
  'collect-review': '/img/icons/task/upload.svg',
  tiktok: '/img/icons/task/tiktok.svg',
  'on-chain': '/img/icons/task/eth.svg',
  telegram: '/img/icons/task/telegram.svg',
  referral: '/img/icons/task/users-plus.svg',
  rollup: '/img/icons/mission/rollup-mission.svg',
  'bug-report': '/img/icons/task/bug.svg',
  'manual-api-reward': '/img/icons/mission/rollup-mission.svg',
  quiz: '/img/icons/task/quiz.svg',
  'graph-indexer': '/img/icons/mission/graph.svg',
  'verify-wallet': '/img/icons/task/wallet-02.svg',
  'zap-value-index': '/img/icons/mission/zap-value-index.svg',
  'zap-launch-create-pool': '/img/icons/task/coins-stacked-03.svg',
  'zap-launch-invest-pool': '/img/icons/task/coins-swap-02.svg',
}
